import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { usersI18n } from './i18n';

export default function UserCreateEditModal({
  Modal,
  Input,
  Dropdown,
  Select,
  Switch,
  show,
  mode = 'create',
  initialData = null,
  roleOptions = [],
  identifierTypeOptions = [],
  areaOptions = [],
  loadingRoles = false,
  loadingAreas = false,
  onClose,
  onSave,
}) {
  const { language } = useLanguage();
  const usersLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: usersI18n });
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    businessAreaId: '',
    identifier: '',
    identifierType: '',
    status: 'active',
    // MFA siempre forzado a true por regla de negocio. El switch está disabled
    // en la UI y el backend ignora el valor que se mande.
    mfa: true,
  });

  useEffect(() => {
    usersI18n.changeLanguage(usersLanguage);
  }, [usersLanguage]);

  const statusOptions = useMemo(
    () => [
      { label: t('users_status_active'), value: 'active' },
      { label: t('users_status_inactive'), value: 'inactive' },
    ],
    [t]
  );

  const findOption = (opts, value) =>
    opts.find((option) => option.value === value) ?? null;

  const normalizeStatus = (value) => {
    const status = String(value || '')
      .trim()
      .toLowerCase();
    return status === 'inactive' || status === 'inactivo'
      ? 'inactive'
      : 'active';
  };

  useEffect(() => {
    if (!show) return;

    if (mode === 'edit' && initialData) {
      setForm({
        name: initialData.name || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        role: initialData.roleValue || initialData.role || '',
        businessAreaId: initialData.businessAreaId || '',
        identifier: initialData.identifier || '',
        identifierType:
          initialData.identifierType || identifierTypeOptions[0]?.value || '',
        status: normalizeStatus(initialData.status),
        // MFA siempre true (regla de negocio); el valor de initialData se ignora.
        mfa: true,
      });
    } else {
      setForm({
        name: '',
        email: '',
        phone: '',
        role: '',
        businessAreaId: '',
        identifier: '',
        identifierType: identifierTypeOptions[0]?.value || '',
        status: 'active',
        mfa: true,
      });
    }
  }, [show, mode, initialData, identifierTypeOptions]);

  const onChange = (key) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const emailValid = useMemo(() => {
    if (!form.email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  }, [form.email]);

  const isValid = useMemo(() => {
    return (
      form.name.trim().length > 1 &&
      emailValid &&
      form.role &&
      form.businessAreaId &&
      (form.status === 'active' || form.status === 'inactive') &&
      form.identifier.trim().length > 3
    );
  }, [form, emailValid]);

  const handleSave = () => {
    if (!isValid) return;
    onSave?.({
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      role: form.role,
      businessAreaId: form.businessAreaId,
      identifier: form.identifier.trim(),
      identifierType: form.identifierType,
      status: form.status,
      mfa: !!form.mfa,
    });
  };

  const title =
    mode === 'edit'
      ? t('users_modal_title_edit')
      : t('users_modal_title_create');
  if (!Modal || !Input) return null;

  const SelectEl = Select || 'select';

  return (
    <Modal
      title={title}
      show={show}
      onClose={onClose}
      primaryBtnTxt={
        mode === 'edit'
          ? t('users_modal_primary_edit')
          : t('users_modal_primary_create')
      }
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid}
      secondaryBtnTxt={t('users_cancel')}
      secondaryBtnAction={onClose}
      ctaAligment='right'
      width='720px'
    >
      <div className='row g-3'>
        <div className='col-12'>
          <Input
            label={t('users_modal_name')}
            required
            value={form.name}
            onChange={onChange('name')}
          />
        </div>

        <div className='col-12'>
          <Input
            label={t('users_modal_email')}
            required
            value={form.email}
            onChange={onChange('email')}
            error={
              form.email && !emailValid ? t('users_modal_email_invalid') : ''
            }
            inputMode='email'
          />
        </div>

        <div className='col-12'>
          <Input
            label={t('users_modal_phone_optional')}
            placeholder={t('users_modal_phone_placeholder')}
            value={form.phone}
            onChange={onChange('phone')}
            inputMode='tel'
          />
        </div>
        <div className='col-12'>
          <div className='row g-2'>
            <div className='col-12 col-md-8'>
              <Input
                label={t('users_modal_identifier')}
                placeholder={t('users_modal_identifier_placeholder')}
                value={form.identifier}
                onChange={onChange('identifier')}
                required
              />
            </div>
            <div className='col-12 col-md-4'>
              {Dropdown ? (
                <Dropdown
                  placeholder={t('users_modal_identifier_type_select')}
                  options={identifierTypeOptions}
                  value={findOption(identifierTypeOptions, form.identifierType)}
                  onChange={(selected) =>
                    setForm((prev) => ({
                      ...prev,
                      identifierType: selected?.value || '',
                    }))
                  }
                  width='100%'
                  label={t('users_modal_identifier_type')}
                  required
                  disabled={loadingRoles || identifierTypeOptions.length === 0}
                />
              ) : (
                <>
                  <label className='form-label'>
                    {t('users_modal_identifier_type')} *
                  </label>
                  <SelectEl
                    className={!Select ? 'form-select' : undefined}
                    value={form.identifierType}
                    onChange={onChange('identifierType')}
                    disabled={
                      loadingRoles || identifierTypeOptions.length === 0
                    }
                  >
                    {identifierTypeOptions.length === 0 ? (
                      <option value=''>
                        {t('users_modal_identifier_type_loading')}
                      </option>
                    ) : (
                      <>
                        <option value=''>
                          {t('users_modal_identifier_type_select')}
                        </option>
                        {identifierTypeOptions.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </>
                    )}
                  </SelectEl>
                </>
              )}
            </div>
          </div>
        </div>
        <div className='col-12'>
          {Dropdown ? (
            <Dropdown
              options={roleOptions}
              value={findOption(roleOptions, form.role)}
              onChange={(opt) =>
                setForm((prev) => ({
                  ...prev,
                  role: opt?.value ?? '',
                }))
              }
              placeholder={
                loadingRoles
                  ? t('users_modal_role_loading')
                  : t('users_modal_role_select')
              }
              width='100%'
              label={t('users_modal_role')}
              required
              disabled={loadingRoles}
            />
          ) : (
            <>
              <label className='form-label'>{t('users_modal_role')} *</label>
              <SelectEl
                className={!Select ? 'form-select' : undefined}
                value={form.role}
                onChange={onChange('role')}
                disabled={loadingRoles}
              >
                {loadingRoles ? (
                  <option value=''>{t('users_modal_role_loading')}</option>
                ) : (
                  <>
                    <option value=''>{t('users_modal_role_select')}</option>
                    {roleOptions.map((roleOption) => (
                      <option key={roleOption.value} value={roleOption.value}>
                        {roleOption.label}
                      </option>
                    ))}
                  </>
                )}
              </SelectEl>
            </>
          )}
          {loadingRoles && (
            <small className='color-gray-600 d-block mt-1'>
              {t('users_modal_role_loading_helper')}
            </small>
          )}
        </div>

        <div className='col-12'>
          {Dropdown ? (
            <Dropdown
              options={areaOptions}
              value={findOption(areaOptions, form.businessAreaId)}
              onChange={(opt) =>
                setForm((prev) => ({
                  ...prev,
                  businessAreaId: opt?.value ?? '',
                }))
              }
              placeholder={
                loadingAreas
                  ? t('users_modal_area_loading')
                  : t('users_modal_area_select')
              }
              width='100%'
              label={t('users_modal_area')}
              required
              disabled={loadingAreas}
            />
          ) : (
            <>
              <label className='form-label'>{t('users_modal_area')} *</label>
              <SelectEl
                className={!Select ? 'form-select' : undefined}
                value={form.businessAreaId}
                onChange={onChange('businessAreaId')}
                disabled={loadingAreas}
              >
                {loadingAreas ? (
                  <option value=''>{t('users_modal_area_loading')}</option>
                ) : (
                  <>
                    <option value=''>{t('users_modal_area_select')}</option>
                    {areaOptions.map((area) => (
                      <option key={area.value} value={area.value}>
                        {area.label}
                      </option>
                    ))}
                  </>
                )}
              </SelectEl>
            </>
          )}
          {loadingAreas && (
            <small className='color-gray-600 d-block mt-1'>
              {t('users_modal_area_loading_helper')}
            </small>
          )}
        </div>

        <div className='col-12'>
          {Dropdown ? (
            <Dropdown
              options={statusOptions}
              value={findOption(statusOptions, form.status)}
              onChange={(opt) =>
                setForm((prev) => ({
                  ...prev,
                  status: opt?.value ?? 'active',
                }))
              }
              placeholder={t('users_modal_status_select')}
              width='100%'
              label={t('users_modal_status')}
              required
            />
          ) : (
            <>
              <label className='form-label'>{t('users_modal_status')} *</label>
              <SelectEl
                className={!Select ? 'form-select' : undefined}
                value={form.status}
                onChange={onChange('status')}
              >
                <option value='active'>{t('users_status_active')}</option>
                <option value='inactive'>{t('users_status_inactive')}</option>
              </SelectEl>
            </>
          )}
        </div>

        <div className='col-12'>
          <div className='p-3 border rounded-3'>
            <div className='d-flex justify-content-between align-items-center'>
              <div>
                <div className='caption fw-medium'>
                  {t('users_modal_mfa_title')}
                </div>
                <div className='mini color-gray-600'>
                  {t('users_modal_mfa_subtitle')}
                </div>
              </div>
              {Switch ? (
                <Switch
                  checked={!!form.mfa}
                  onChange={(value) =>
                    setForm((prev) => ({ ...prev, mfa: !!value }))
                  }
                  disabled
                />
              ) : (
                <input
                  type='checkbox'
                  className='form-check-input'
                  checked={!!form.mfa}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, mfa: event.target.checked }))
                  }
                  disabled
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
